/**
 * The one place that decides which identifier a user is shown for a record
 * that came from an ERP.
 *
 * Every record we import carries up to two external identifiers. `externalId`
 * is whatever the ERP's own primary key happens to be — often an opaque
 * internal number that means nothing to anyone outside that system.
 * `externalId2` is the id the customer actually uses day to day: the SKU on
 * the shelf label, the invoice number on the paper invoice. So the display
 * rule is `externalId2 ?? externalId ?? <the record's own id>`, and the last
 * step exists so a record with neither is still addressable rather than
 * rendering blank.
 *
 * That chain was hand-written in roughly fifty places before PD-13667, and
 * the copies had drifted in three ways that all reached a user:
 *
 *   1. Some omitted the external ids entirely and printed our internal
 *      primary key. The quote and invoice carts did this, which is what the
 *      ticket was raised for — a rep saw `17251` where the shop beside it
 *      said `204626`.
 *   2. One had the chain INVERTED, preferring the ERP's opaque id over the
 *      customer-facing one for every tenant that populates both.
 *   3. Half used `??` and half used `||`, which disagree about an empty
 *      string. An imported-but-blank `externalId2` is absent in every sense
 *      a user cares about, so this helper treats it as absent — matching
 *      what the `||` sites already assumed and correcting the `??` ones,
 *      which would have rendered an empty cell.
 *
 * Kept in `libs/shared` rather than `libs/core` because the same rule has to
 * hold on the server: PDF generation, the statement builder and several list
 * endpoints all format these identifiers before they reach a client.
 */

/**
 * What to show when a record has neither external id.
 *
 * - `id` — the record's own id as plain text (`"17251"`). Used for items,
 *   clients and anything whose identifier is rendered as a bare value.
 * - `hash` — the record's own id prefixed (`"#4821"`). Used for documents —
 *   orders, invoices, credit memos — where the surrounding copy reads
 *   "Invoice #4821" and a bare number would look like an external id.
 * - `none` — `null`. Used where the caller has its own empty-state handling
 *   (a "-" placeholder, a hidden row) and would rather render nothing than
 *   an internal id.
 */
export type ExternalIdFallback = 'id' | 'hash' | 'none';

/** The identifier fields this helper reads, in whatever casing a caller has. */
export interface ExternalIdSource {
	externalId?: string | null;
	externalId2?: string | null;
	/**
	 * The record's own primary key. Accepts a number or a string because the
	 * shape reaches here from entities (`number`), from list rows (`string`,
	 * since MySQL BIGINT deserializes as text) and from DTOs that spell it
	 * `_id`.
	 */
	id?: number | string | null;
}

export interface DisplayExternalIdOptions {
	/** Default `'id'`. */
	fallback?: ExternalIdFallback;
}

/**
 * An external id counts only when it is present AND non-blank.
 *
 * Importers write `''` about as often as they write NULL for a field the
 * source system left empty, and a blank cell is never what the caller wanted
 * — it would hide the record's identity entirely.
 */
function present(value: string | null | undefined): value is string {
	return value != null && value.trim() !== '';
}

/**
 * The identifier to display for an ERP-sourced record.
 *
 * @param source - The record, or any object carrying its identifier fields.
 * @param options - Which fallback to use when both external ids are absent.
 * @returns The identifier, or `null` when the record has no usable id at all
 *   (and always when `fallback` is `'none'` and no external id is set).
 */
export function displayExternalId(
	source: ExternalIdSource | null | undefined,
	options?: DisplayExternalIdOptions,
): string | null {
	if (source == null) {
		return null;
	}

	if (present(source.externalId2)) {
		return source.externalId2.trim();
	}

	if (present(source.externalId)) {
		return source.externalId.trim();
	}

	const fallback = options?.fallback ?? 'id';

	if (fallback === 'none') {
		return null;
	}

	const id = source.id;

	// `0` is a legitimate id in no table we have, but it is also what a
	// half-built object carries, so it is excluded deliberately rather than by
	// a truthiness check that would also drop a valid `"0"`.
	if (id == null || id === '' || id === 0) {
		return null;
	}

	const rendered = String(id);

	return fallback === 'hash' ? `#${rendered}` : rendered;
}
